import { FormEvent, useState } from 'react';
import { useAuthContext } from 'src/contexts/AuthContext';
import { useModal } from '../context/ModalContext';

export function ForgotPanel() {
  const { forgotPasswordSendCode, forgotPasswordUpdatePassword } =
    useAuthContext();
  const { open } = useModal();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSendCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await forgotPasswordSendCode(email.trim());
      setStep('reset');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not send the reset code.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResetPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      // On success this auto-signs-in; the auth state flips and the whole
      // marketing tree unmounts, so there is no modal to close manually.
      await forgotPasswordUpdatePassword(
        email.trim(),
        code.trim(),
        newPassword,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not reset the password.',
      );
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div className="mb-6">
        <h5>Reset Password</h5>
        <p className="mb-0">
          {step === 'email'
            ? 'Enter your email and we will send you a reset code.'
            : 'Enter the code we emailed you and your new password.'}
        </p>
      </div>

      {step === 'email' ? (
        <form className="contact-form" onSubmit={handleSendCode}>
          <div className="row gx-gs gy-5">
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="forgot-email">
                  Email
                </label>
                <div className="form-control-wrap">
                  <input
                    type="email"
                    id="forgot-email"
                    name="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-control form-control-xl"
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            </div>
            {error && (
              <div className="col-12">
                <p className="text-danger mb-0" role="alert">
                  {error}
                </p>
              </div>
            )}
            <div className="col-12">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-outline-light btn-lg btn-block opacity-80">
                {isSubmitting ? 'Sending…' : 'Send Reset Code'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <form className="contact-form" onSubmit={handleResetPassword}>
          <div className="row gx-gs gy-5">
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="forgot-code">
                  Reset Code
                </label>
                <div className="form-control-wrap">
                  <input
                    type="text"
                    id="forgot-code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="form-control form-control-xl"
                    placeholder="Enter the code from your email"
                  />
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="forgot-new-password">
                  New Password
                </label>
                <div className="form-control-wrap">
                  <input
                    type="password"
                    id="forgot-new-password"
                    name="newPassword"
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-control form-control-xl"
                    placeholder="Enter your new password"
                  />
                </div>
              </div>
            </div>
            {error && (
              <div className="col-12">
                <p className="text-danger mb-0" role="alert">
                  {error}
                </p>
              </div>
            )}
            <div className="col-12">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-outline-light btn-lg btn-block opacity-80">
                {isSubmitting ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="mt-6 text-content text-center">
        {' '}
        Remembered your password?{' '}
        <button
          type="button"
          onClick={() => open('login')}
          className="link link-primary link-hover-title btn btn-link p-0">
          login
        </button>
      </div>
    </>
  );
}
